import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useSocket } from '../contexts/SocketContext';
import api from '../services/api';
import AppointmentForkBanner from './AppointmentForkBanner';
import EditAppointmentModal from './EditAppointmentModal';

const AppointmentCard = ({ appointment: initialAppointment, onUpdate, onDelete }) => {
  const { user } = useAuth();
  const { socket } = useSocket();
  const [appointment, setAppointment] = useState(initialAppointment);
  // const [showAllComments, setShowAllComments] = useState(false);
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [commentInput, setCommentInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  useEffect(() => {
    setAppointment(initialAppointment);
  }, [initialAppointment]);

  useEffect(() => {
    if (!socket || !appointment) return;

    const appointmentId = appointment.id; // Capture ID to avoid stale closure

    // Join this appointment's room
    socket.emit('join-appointment', { appointmentId });

    // Listen for real-time updates
    const handleParticipantJoined = async (data) => {
      if (data.appointmentId === appointmentId) {
        // Fetch fresh data from server to get accurate participant count
        try {
          const response = await api.get(`/appointments/${appointmentId}`);
          setAppointment(response.data);
        } catch (error) {
          console.error('Error fetching updated appointment:', error);
          // Fallback to increment if fetch fails
          setAppointment(prev => ({
            ...prev,
            participant_count: prev.participant_count + 1
          }));
        }
      }
    };

    const handleParticipantLeft = async (data) => {
      if (data.appointmentId === appointmentId) {
        // Fetch fresh data from server to get accurate participant count
        try {
          const response = await api.get(`/appointments/${appointmentId}`);
          setAppointment(response.data);
        } catch (error) {
          console.error('Error fetching updated appointment:', error);
          // Fallback to decrement if fetch fails
          setAppointment(prev => ({
            ...prev,
            participant_count: Math.max(0, prev.participant_count - 1)
          }));
        }
      }
    };

    const handleReactionToggled = (data) => {
      if (data.appointmentId === appointmentId) {
        setAppointment(prev => ({
          ...prev,
          reactions_summary: data.reactionsSummary
        }));
      }
    };

    const handleCommentAdded = (data) => {
      if (data.appointmentId === appointmentId) {
        setAppointment(prev => ({
          ...prev,
          recent_comments: [...(prev.recent_comments || []), data.comment].slice(-3)
        }));
      }
    };

    socket.on('participant-joined', handleParticipantJoined);
    socket.on('participant-left', handleParticipantLeft);
    socket.on('reaction-toggled', handleReactionToggled);
    socket.on('comment-added', handleCommentAdded);

    return () => {
      socket.emit('leave-appointment', { appointmentId });
      socket.off('participant-joined', handleParticipantJoined);
      socket.off('participant-left', handleParticipantLeft);
      socket.off('reaction-toggled', handleReactionToggled);
      socket.off('comment-added', handleCommentAdded);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, appointment?.id]);

  const formatRelativeTime = (date) => {
    const now = new Date();
    const startTime = new Date(date);
    const diffMs = startTime - now;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMs < 0) return 'Started';
    if (diffMins < 60) return `In ${diffMins} min`;
    if (diffHours < 24) return `In ${diffHours} hour${diffHours !== 1 ? 's' : ''}`;
    return `In ${diffDays} day${diffDays !== 1 ? 's' : ''}`;
  };

  const formatAbsoluteTime = (startDate, duration) => {
    const start = new Date(startDate);
    const end = new Date(start.getTime() + duration * 60000);

    const dateOptions = { weekday: 'short', month: 'short', day: 'numeric' };
    const timeOptions = { hour: 'numeric', minute: '2-digit', timeZoneName: 'short' };

    const dateStr = start.toLocaleDateString('en-US', dateOptions);
    const startTimeStr = start.toLocaleTimeString('en-US', timeOptions);
    const endTimeStr = end.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

    return `${dateStr}, ${startTimeStr} - ${endTimeStr}`;
  };

  const handleJoinToggle = async () => {
    try {
      setLoading(true);
      const response = await api.post(`/appointments/${appointment.id}/join`);
      const joined = response.data.joined;

      // Only update is_user_participant, let Socket.IO handle participant_count
      setAppointment(prev => ({
        ...prev,
        is_user_participant: joined
      }));
    } catch (error) {
      console.error('Error toggling join:', error);
      alert(error.response?.data?.error || 'Failed to join/leave appointment');
    } finally {
      setLoading(false);
    }
  };

  const handleReaction = async (emoji) => {
    try {
      const response = await api.post(`/appointments/${appointment.id}/reactions`, { emoji });
      setAppointment(prev => ({
        ...prev,
        reactions_summary: response.data.reactionsSummary
      }));
    } catch (error) {
      console.error('Error toggling reaction:', error);
    }
  };

  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    if (!commentInput.trim()) return;

    try {
      await api.post(`/appointments/${appointment.id}/comments`, {
        content: commentInput.trim()
      });

      // Don't add to local state - let Socket.IO event handle it
      // This prevents duplicates since backend emits to all users including sender

      setCommentInput('');
    } catch (error) {
      console.error('Error adding comment:', error);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this appointment? All participants will lose access to it.')) {
      return;
    }

    try {
      await api.delete(`/appointments/${appointment.id}`);
      if (onDelete) {
        onDelete(appointment.id);
      }
    } catch (error) {
      console.error('Error deleting appointment:', error);
      alert(error.response?.data?.error || 'Failed to delete appointment');
    }
  };

  const handleUpdate = (updatedAppointment) => {
    setAppointment(updatedAppointment);
    if (onUpdate) {
      onUpdate(updatedAppointment);
    }
  };

  if (!appointment) return null;

  const isCreator = user && appointment.creator_user_id === user.id;
  const reactionEmojis = ['👍', '✅', '🔥', '💪', '🎯'];

  return (
    <div className="bg-white rounded-lg shadow-md p-6 mb-4">
      {/* Fork Banner */}
      <AppointmentForkBanner forkedToId={appointment.forked_to_id} />

      {/* Header with Creator */}
      <div className="flex items-start gap-3 mb-4">
        <div className="flex-1">
          <h3 className="font-semibold text-gray-900">{appointment.creator_name}</h3>
          <div className="text-sm text-gray-600">
            <span className="font-medium text-indigo-600">{formatRelativeTime(appointment.start_time)}</span>
            {' • '}
            <span>{formatAbsoluteTime(appointment.start_time, appointment.duration_minutes)}</span>
          </div>
        </div>
        {isCreator && (
          <div className="flex gap-2">
            <button
              onClick={() => setShowEditModal(true)}
              className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md"
            >
              Edit
            </button>
            <button
              onClick={handleDelete}
              className="px-3 py-1 text-sm bg-red-100 hover:bg-red-200 text-red-700 rounded-md"
            >
              Delete
            </button>
          </div>
        )}
      </div>

      {/* Description */}
      <div className="mb-4">
        <p className="text-gray-800 whitespace-pre-wrap">
          {showFullDescription || appointment.description.length <= 200
            ? appointment.description
            : `${appointment.description.substring(0, 200)}...`}
        </p>
        {appointment.description.length > 200 && (
          <button
            onClick={() => setShowFullDescription(!showFullDescription)}
            className="text-sm text-indigo-600 hover:text-indigo-800 mt-1"
          >
            {showFullDescription ? 'Show less' : 'Show more'}
          </button>
        )}
      </div>

      {/* Participant Count */}
      <div className="mb-4">
        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-indigo-100 text-indigo-800">
          👥 {appointment.participant_count} joined
        </span>
      </div>

      {/* Join Button */}
      {!isCreator && (
        <div className="mb-4">
          <button
            onClick={handleJoinToggle}
            disabled={loading}
            className={`px-4 py-2 rounded-md font-medium transition-colors ${
              appointment.is_user_participant
                ? 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                : 'bg-indigo-600 text-white hover:bg-indigo-700'
            } disabled:opacity-50`}
          >
            {loading ? 'Loading...' : (appointment.is_user_participant ? 'Leave' : 'Join this block')}
          </button>
        </div>
      )}

      {/* Reactions */}
      <div className="flex flex-wrap gap-2 mb-4">
        {reactionEmojis.map(emoji => {
          const reaction = (appointment.reactions_summary || []).find(r => r.emoji === emoji);
          const count = reaction?.count || 0;
          const userReacted = reaction?.reacted_by_current_user || false;

          if (count === 0 && !userReacted) return null;

          return (
            <button
              key={emoji}
              onClick={() => handleReaction(emoji)}
              className={`px-3 py-1 rounded-full text-sm transition-colors ${
                userReacted
                  ? 'bg-indigo-100 border-2 border-indigo-500'
                  : 'bg-gray-100 hover:bg-gray-200'
              }`}
            >
              {emoji} {count > 0 && count}
            </button>
          );
        })}
        {/* Show all emoji options when hovering or clicking */}
        {reactionEmojis.map(emoji => {
          const reaction = (appointment.reactions_summary || []).find(r => r.emoji === emoji);
          const count = reaction?.count || 0;

          if (count > 0) return null; // Already shown above

          return (
            <button
              key={`add-${emoji}`}
              onClick={() => handleReaction(emoji)}
              className="px-2 py-1 rounded-full text-sm bg-gray-50 hover:bg-gray-100 opacity-60 hover:opacity-100"
            >
              {emoji}
            </button>
          );
        })}
      </div>

      {/* Comments */}
      <div className="border-t pt-4">
        <h4 className="font-semibold text-gray-900 mb-3">Comments</h4>

        {/* Comment List */}
        {appointment.recent_comments && appointment.recent_comments.length > 0 ? (
          <div className="space-y-2 mb-3">
            {appointment.recent_comments.map((comment) => (
              <div key={comment.id} className="bg-gray-50 rounded-lg px-3 py-2">
                <p className="text-sm font-medium text-gray-900">{comment.user_name}</p>
                <p className="text-sm text-gray-700">{comment.content}</p>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500 mb-3">No comments yet. Be the first to comment!</p>
        )}

        {/* Comment Input */}
        <form onSubmit={handleCommentSubmit} className="flex gap-2">
          <input
            type="text"
            value={commentInput}
            onChange={(e) => setCommentInput(e.target.value)}
            placeholder="Add a comment..."
            className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
          />
          <button
            type="submit"
            disabled={!commentInput.trim()}
            className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
          >
            Post
          </button>
        </form>
      </div>

      {/* Edit Modal */}
      <EditAppointmentModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        appointment={appointment}
        onUpdated={handleUpdate}
      />
    </div>
  );
};

export default AppointmentCard;
